import React, { ReactNode } from 'react'
import { css, cx } from '@emotion/css'
import { CsCloseIcon } from '../../legacy-design-system/icons'
import { theme } from '@contrast-security-inc/design-system-foundations'

interface RenderFooterProps {
  readonly onClick?: () => void
  readonly className: string
}

export interface PanelProps {
  readonly dataTestid?: string
  readonly onClose?: () => void
  readonly onFooterClick?: () => void
  readonly renderHeader?: () => React.ReactElement<any>

  /**
   * Render additional content before the close icon for this panel
   */
  readonly renderPanelActions?: (props: {
    readonly actionsIconCss: string
  }) => any

  readonly renderBody?: () => ReactNode | ReactNode[]
  readonly renderFooter?: (
    renderFooterProps: RenderFooterProps
  ) => React.ReactElement<any>
  readonly className?: string
  readonly closeIconClassName?: string
  readonly boxShadow?: boolean
  readonly bodyContainerCss?: string
}

const headerContainerCss = css`
  padding: 0px 20px 30px 30px;
  border-bottom: 1px solid ${theme.color.neutral30};
  background-color: ${theme.color.white};
`

const footerContainerCss = css`
  padding: 10px 115px 15px 105px;
  text-align: left;
  background-color: ${theme.color.neutral10};
`

const actionsContainerCss = css`
  display: flex;
  justify-content: flex-end;
  padding-right: 20px;
  padding-top: 20px;
`

const actionsIconCss = css`
  background-color: ${theme.color.white};
  margin-right: 20px;
  cursor: pointer;
  color: ${theme.color.neutral40};
  z-index: 1002;
`

const foooterBtnCss = css`
  display: inline;
`

export function Panel({
  dataTestid,
  onClose,
  onFooterClick,
  renderHeader,
  renderPanelActions,
  renderBody,
  renderFooter,
  closeIconClassName,
  className,
  boxShadow = true,
  bodyContainerCss
}: PanelProps) {
  const footerButton: ReactNode = !!renderFooter
    ? renderFooter({
        className: foooterBtnCss,
        onClick: onFooterClick
      })
    : undefined

  const mainContainerCss = css`
    overflow-y: auto;
    width: 425px;
    border: 1px solid ${theme.color.neutral20};
    background-color: ${theme.color.neutral10};
    ${boxShadow
      ? 'box-shadow: -15px -1px 15px -10px rgba(0, 0, 0, 0.1);' +
        '-webkit-box-shadow: -15px -1px 15px -10px rgba(0, 0, 0, 0.1); ' +
        '-moz-box-shadow: -15px -1px 15px -10px rgba(0, 0, 0, 0.1);'
      : ''};
  `

  const closeIconCss = cx(
    css`
      height: 13px;
      width: 13px;
    `,
    closeIconClassName
  )

  return (
    <div data-testid={dataTestid} className={cx(mainContainerCss, className)}>
      <div className={actionsContainerCss}>
        {!!renderPanelActions &&
          renderPanelActions({
            actionsIconCss
          })}
        <CsCloseIcon
          onClick={onClose}
          size={12}
          className={closeIconCss}
          data-testid="sliding-tutorial-close-button"
        />
      </div>
      {renderHeader && (
        <div className={headerContainerCss}>{renderHeader()}</div>
      )}
      <div className={bodyContainerCss}>{renderBody && renderBody()}</div>
      {renderFooter && (
        <div className={footerContainerCss} children={footerButton} />
      )}
    </div>
  )
}
